<template>
  <component :is="navbarFixed ? 'a-affix' : 'div'" :offset-top="top">
    <header class="dashboard-header">
      <div class="dashboard-header__top">
        <div class="dashboard-header__left">
          <!-- <router-link to="/" class="dashboard-header__logo" @click.prevent="goHome">
            <img
              v-if="companyLogo"
              :src="companyLogo"
              :alt="companyName"
              class="dashboard-header__logo-img"
            />
            <div v-else class="dashboard-header__brand">
              <span class="dashboard-header__logo-text">{{ companyName }}</span>
              <span v-if="companyTagline" class="dashboard-header__logo-tagline">
                {{ companyTagline }}
              </span>
            </div>
          </router-link> -->

          <button
            type="button"
            class="dashboard-header__toggle sidebar-toggler"
            aria-label="Toggle sidebar"
            @click="$emit('toggleSidebar', !sidebarCollapsed), resizeEventHandler()"
          >
            <i class="fas fa-bars" />
          </button>
        </div>

        <div class="dashboard-header__decor" aria-hidden="true">
          <span class="dashboard-header__decor-stripe" />
        </div>

        <div class="dashboard-header__right header-control">
          <a-button type="link" class="dashboard-header__action">
            <SwitchLang />
          </a-button>
          <a-button
            type="link"
            class="dashboard-header__action"
            ref="secondarySidebarTriggerBtn"
            @click="$emit('toggleSettingsDrawer', true)"
          >
            <i class="fas fa-cog" />
          </a-button>
          <UserInform variant="dashboard" />
        </div>
      </div>

      <div class="dashboard-header__breadcrumb">
        <Breadcrumb />
      </div>
    </header>
  </component>
</template>

<script lang="ts" setup>
import { ref, onMounted, computed } from "vue";
import { useRouter } from "vue-router";
import SwitchLang from "@/components/element/lang/SwitchLang.vue";
import Breadcrumb from "@/components/breadcrumb/Breadcrumb.vue";
import UserInform from "@/layouts/dashboard/nav/user/UserInfor.vue";
import commons from "@/utils/Common";

interface Props {
  navbarFixed: boolean;
  sidebarCollapsed: boolean;
  notificationsData?: any;
}

withDefaults(defineProps<Props>(), {
  navbarFixed: false,
  sidebarCollapsed: false,
  notificationsData: () => [],
});

defineEmits(["toggleSidebar", "toggleSettingsDrawer"]);

const router = useRouter();
const top = ref(0);
const company = ref<Record<string, string>>({});

const companyName = computed(
  () => company.value.sub_name || company.value.subName || "KIMAHOME"
);
const companyTagline = computed(
  () => company.value.summary || ""
);
const companyLogo = computed(
  () => company.value.logo || company.value.urlLogo || ""
);

const resizeEventHandler = () => {
  top.value = top.value ? 0 : -0.01;
};

const goHome = () => {
  router.push("/");
};

const loadCompany = () => {
  const data = commons.getCokiesCompany();
  if (data) {
    company.value = data;
  }
};

onMounted(() => {
  loadCompany();
  window.addEventListener("resize", resizeEventHandler);
});
</script>

<style lang="scss" scoped>
.dashboard-header {
  margin: 0;
  background: var(--bg-white, #fff);
  border-bottom: none;
}

.dashboard-header__top {
  position: relative;
  display: flex;
  align-items: stretch;
  min-height: 64px;
  padding: 0;
  overflow: hidden;
  background: var(--bg-white, #fff);
  border-bottom: 1px solid var(--border-light, #e5e5e5);
}

.dashboard-header__left {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 14px;
  flex-shrink: 0;
  padding: 0 20px;
  background: var(--bg-white, #fff);
}

.dashboard-header__logo {
  display: inline-flex;
  align-items: center;
  text-decoration: none;
  cursor: pointer;
}

.dashboard-header__logo-img {
  display: block;
  height: 36px;
  width: auto;
  max-width: 180px;
  object-fit: contain;
}

.dashboard-header__brand {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1.1;
}

.dashboard-header__logo-text {
  font-family: var(--heading-font, "libre-baskerville", serif);
  font-size: 22px;
  font-weight: 700;
  color: var(--primary-dark, #102542);
  letter-spacing: 0.12em;
  text-transform: uppercase;
  white-space: nowrap;
}

.dashboard-header__logo-tagline {
  margin-top: 4px;
  font-family: var(--nav-font, "libre-baskerville", serif);
  font-size: 10px;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: var(--default-header-color, var(--primary-gold, #b19663));
  white-space: nowrap;
}

.dashboard-header__toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: var(--primary-dark, #102542);
  font-size: 18px;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;

  &:hover {
    background: color-mix(in srgb, var(--primary-gold, #d4af37) 16%, #fff);
    color: var(--default-header-color, #b19663);
  }
}

.dashboard-header__decor {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 1;
}

.dashboard-header__decor-stripe {
  position: absolute;
  top: 0;
  right: 0;
  width: 62%;
  height: 100%;
  background: linear-gradient(
    105deg,
    transparent 0%,
    transparent 12%,
    rgba(244, 208, 63, 0.35) 18%,
    var(--primary-gold-light, #f4d03f) 24%,
    var(--primary-gold, #d4af37) 36%,
    var(--default-header-color, #b19663) 48%,
    #1a3a5c 68%,
    var(--primary-dark, #102542) 85%,
    #0a1829 100%
  );
  clip-path: polygon(12% 0, 100% 0, 100% 100%, 0 100%);
  opacity: 0.96;
}

.dashboard-header__right {
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: row-reverse;
  align-items: center;
  gap: 6px;
  margin-left: auto;
  padding: 0 20px 0 48px;
  min-width: 0;
}

.dashboard-header__action {
  height: auto;
  padding: 0 8px;
  margin: 0;
  box-shadow: none;
  color: var(--default-header-color, var(--primary-gold, #b19663)) !important;

  i {
    font-size: 1.1rem;
    color: var(--default-header-color, var(--primary-gold, #b19663));
  }

  &:hover {
    color: var(--primary-gold-light, #f4d03f) !important;

    i {
      color: var(--primary-gold-light, #f4d03f);
    }
  }
}

.dashboard-header__right :deep(.menu-nav) {
  display: flex;
  align-items: center;
}

.dashboard-header__right :deep(.el-dropdown-link) {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
}

.dashboard-header__right :deep(.user-name) {
  padding: 0 !important;
  margin: 0 !important;
  color: var(--default-header-color, var(--primary-gold, #b19663)) !important;
  font-family: var(--nav-font, "libre-baskerville", serif);
  font-size: 14px;
  font-weight: 500;
  background: none !important;
}

.dashboard-header__right :deep(.el-avatar) {
  background: rgba(177, 150, 99, 0.25);
  border: 1px solid rgba(177, 150, 99, 0.5);
  color: var(--default-header-color, var(--primary-gold, #b19663));
}

.dashboard-header__right :deep(.dashboard-user-chevron) {
  color: var(--default-header-color, var(--primary-gold, #b19663));
  font-size: 12px;
  opacity: 0.9;
}

.dashboard-header__right :deep(.language-selector) {
  color: var(--default-header-color, var(--primary-gold, #b19663));
  font-family: var(--nav-font, "libre-baskerville", serif);
  font-size: 12px;
  letter-spacing: 0.04em;
  cursor: pointer;

  span {
    color: var(--default-header-color, var(--primary-gold, #b19663));
  }

  .bold {
    color: var(--primary-gold-light, #f4d03f);
    font-weight: 600;
  }
}

.dashboard-header__breadcrumb {
  padding: 12px 20px 8px;
  background: var(--bg-light, #f9f9f9);
  border-top: 1px solid var(--border-light, #e5e5e5);
}

.dashboard-header__breadcrumb :deep(.break-crumb) {
  padding-top: 0 !important;
}

@media (max-width: 767px) {
  .dashboard-header__left {
    padding: 0 12px;
    gap: 8px;
  }

  .dashboard-header__right {
    padding: 0 12px 0 24px;
    gap: 0;
  }

  .dashboard-header__right :deep(.user-name),
  .dashboard-header__logo-tagline {
    display: none;
  }

  .dashboard-header__logo-text {
    font-size: 16px;
    letter-spacing: 0.08em;
  }

  .dashboard-header__breadcrumb {
    padding: 10px 12px 6px;
  }
}
</style>

<style>
.dark-mode .dashboard-header__right .dashboard-header__action,
.dark-mode .dashboard-header__right .dashboard-header__action i {
  color: var(--default-header-color, #b19663) !important;
}
</style>
